import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { Input } from '@heyroot/ui/primitives/Input';
import { Select } from '@heyroot/ui/primitives/Select';
import { SkeletonTable } from '@heyroot/ui/primitives/Skeleton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import {
  fetchBusinessTypes,
  fetchBusinessTypeCategories,
  queryKeys,
  type BusinessTypeFilters,
} from '../lib/api';

/** `full_service_restaurant` -> `Full Service Restaurant`. Kept exported so
 * this stays a non-breaking rewrite for any sibling that still imports it;
 * registry rows carry a real `name` column and must not use it here. */
export function humanizeBusinessType(v: string): string {
  return v
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

function TriStateFilter({
  label,
  value,
  trueLabel,
  falseLabel,
  onSet,
  onClear,
}: {
  label: string;
  value: boolean | undefined;
  trueLabel: string;
  falseLabel: string;
  onSet: (v: boolean) => void;
  onClear: () => void;
}) {
  return (
    // Width lives on a wrapper, not on Select: the primitive bakes in `w-full`,
    // and a competing `w-*` in its className would be resolved by Tailwind's
    // own utility ordering rather than by class order -- which rendered all
    // four filters full-width, one per row.
    <div className="w-44">
      <Select
        aria-label={label}
        value={value === undefined ? '' : String(value)}
        onChange={(e) => {
          if (e.target.value === '') onClear();
          else onSet(e.target.value === 'true');
        }}
      >
        <option value="">{label}: all</option>
        <option value="true">{trueLabel}</option>
        <option value="false">{falseLabel}</option>
      </Select>
    </div>
  );
}

export function BusinessTypesListPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<BusinessTypeFilters>({});
  const [searchDraft, setSearchDraft] = useState('');

  const { data, isPending, isError, error } = useQuery({
    queryKey: queryKeys.businessTypes(filters),
    queryFn: () => fetchBusinessTypes(filters),
  });
  const { data: cats } = useQuery({
    queryKey: queryKeys.businessTypeCategories,
    queryFn: fetchBusinessTypeCategories,
  });

  const rows = data?.rows ?? [];
  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((v) => v !== undefined && v !== ''),
    [filters],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-heading-2 text-primary">Business Types</h1>
          <p className="mt-1 text-body-sm text-muted">
            Per-type module, accounting, and role-template defaults.
          </p>
        </div>
        <Button onClick={() => void navigate({ to: '/business-types/new' })}>
          New business type
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <form
          className="min-w-56"
          onSubmit={(e) => {
            e.preventDefault();
            setFilters((f) => {
              const next = { ...f };
              if (searchDraft) next.search = searchDraft;
              else delete next.search;
              return next;
            });
          }}
        >
          <Input
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="Search name or slug"
            aria-label="Search business types"
          />
        </form>
        {/* Sized wrapper for the same reason as TriStateFilter above. */}
        <div className="w-48">
          <Select
            aria-label="Category"
            value={filters.categoryId ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setFilters((f) => {
                const next = { ...f };
                if (v) next.categoryId = v;
                else delete next.categoryId;
                return next;
              });
            }}
          >
            <option value="">Category: all</option>
            {(cats?.rows ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <TriStateFilter
          label="Status"
          value={filters.isActive}
          trueLabel="Active"
          falseLabel="Deactivated"
          onSet={(v) => setFilters((f) => ({ ...f, isActive: v }))}
          onClear={() =>
            setFilters((f) => {
              const next = { ...f };
              delete next.isActive;
              return next;
            })
          }
        />
        <TriStateFilter
          label="Origin"
          value={filters.isSystem}
          trueLabel="System"
          falseLabel="Custom"
          onSet={(v) => setFilters((f) => ({ ...f, isSystem: v }))}
          onClear={() =>
            setFilters((f) => {
              const next = { ...f };
              delete next.isSystem;
              return next;
            })
          }
        />
        <TriStateFilter
          label="Signup"
          value={filters.showAtSignup}
          trueLabel="Shown at signup"
          falseLabel="Hidden at signup"
          onSet={(v) => setFilters((f) => ({ ...f, showAtSignup: v }))}
          onClear={() =>
            setFilters((f) => {
              const next = { ...f };
              delete next.showAtSignup;
              return next;
            })
          }
        />
      </div>

      {isError && !isPending && (
        <div
          role="alert"
          className="rounded-control border border-danger bg-danger-pale text-danger-text text-body-sm p-4"
        >
          {error instanceof Error ? error.message : 'Failed to load business types.'}
        </div>
      )}

      {!isPending && !isError && rows.length === 0 && (
        <EmptyState
          title={hasActiveFilters ? 'No matches' : 'No business types'}
          description={
            hasActiveFilters
              ? 'No business type matches these filters.'
              : 'Create a business type to define module, role and accounting defaults.'
          }
        />
      )}

      {(isPending || (!isError && rows.length > 0)) && (
        <div className="overflow-x-auto">
          <Table dense>
            <TableHead>
              <tr>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Category</TableHeaderCell>
                <TableHeaderCell>Published version</TableHeaderCell>
                <TableHeaderCell className="text-right">Modules</TableHeaderCell>
                <TableHeaderCell className="text-right">Roles</TableHeaderCell>
                <TableHeaderCell>Signup</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
              </tr>
            </TableHead>
            <TableBody>
              {isPending ? (
                <SkeletonTable rows={6} cols={7} />
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={() =>
                      void navigate({
                        to: '/business-types/$businessType',
                        params: { businessType: row.slug },
                      })
                    }
                  >
                    <TableCell className="text-body-sm-emphasis text-primary">
                      {row.name}
                      <div className="text-eyebrow text-muted">{row.slug}</div>
                    </TableCell>
                    <TableCell className="text-secondary">
                      {row.categoryName ?? <span className="text-muted">—</span>}
                    </TableCell>
                    <TableCell>
                      {row.publishedVersionNumber === null ? (
                        <Badge variant="warning">Unpublished</Badge>
                      ) : (
                        <span className="tabular-nums text-secondary">
                          v{row.publishedVersionNumber}
                        </span>
                      )}
                      {row.hasDraft && (
                        <Badge variant="info" className="ml-2">
                          Draft
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <span className={row.moduleCount === 0 ? 'text-muted' : undefined}>
                        {row.moduleCount}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <span className={row.roleCount === 0 ? 'text-muted' : undefined}>
                        {row.roleCount}
                      </span>
                    </TableCell>
                    <TableCell>
                      {row.showAtSignup ? (
                        <Badge variant="success">Signup</Badge>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.isActive ? (
                        <Badge variant="neutral">Active</Badge>
                      ) : (
                        <Badge variant="danger">Deactivated</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
